import React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';

// A small presentational component that renders wishes as a postcard to Santa
// Props:
//  - wishes: string (required)
//  - receiverName: string | undefined (optional, used in header)
const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    padding: theme.spacing(3),
    backgroundColor: '#fffdf7', // warm paper color
    border: '2px solid #d32f2f', // festive red border
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  stamp: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    width: 56,
    height: 56,
    backgroundImage: 'url(/santa-claus.png)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '2px dashed rgba(0,0,0,0.15)',
    borderRadius: 6,
    opacity: 0.9,
  },
  lines: {
    // light ruled lines like a postcard
    position: 'absolute',
    inset: 0,
    backgroundImage: `repeating-linear-gradient(
      to bottom,
      transparent 0px,
      transparent 28px,
      rgba(0,0,0,0.06) 29px
    )`,
    pointerEvents: 'none',
  },
  addressBlock: {
    position: 'absolute',
    // place it like on a real postcard — right side for the address
    top: theme.spacing(13), // below header and clear of the stamp
    right: theme.spacing(1.5),
    width: 130,
    textAlign: 'right',
    zIndex: 1,
    pointerEvents: 'none', // do not block editing in the text area
    border: '2px dashed rgba(0,0,0,0.15)',
    borderRadius: 6,
    opacity: 0.9,
  },
  addressLine: {
    lineHeight: '22px',
    color: '#b71c1c',
    fontFamily: 'inherit',
    paddingLeft: 5,
    paddingRight: 10
  },
  content: {
    position: 'relative',
    zIndex: 1,
    color: theme.palette.text.primary,
    whiteSpace: 'pre-line',
  },
  header: {
    color: '#b71c1c',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  textStyle: {
    width: '80%',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    resize: 'vertical',
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: '1rem',
    lineHeight: '29px', // matches the ruled line spacing
    minHeight: 4 * 29,
    // leave room on the right side for the address block like a real postcard
    paddingRight: 240,
    // Prevent long words/URLs from overflowing the postcard border
    wordBreak: 'break-word',
    wordWrap: 'break-word', // legacy alias for some browsers
    overflowWrap: 'anywhere',
  },
  footer: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

export default function WishesPostcard({
                                         wishes,
                                         from,
                                         // editable mode props
                                         editable = false,
                                         value,
                                         onChange,
                                         disabled = false,
                                       }) {
  const classes = useStyles();

  let wishesPlaceholder =
    `Dear Santa, this year I was very good/bad boy/girl, but I definitely promise that next year I would be better/worse!
    
I would be really glad to receive ...

Thank you very much dear Santa, merry whatever you celebrating and have a great new year!`;

  return (
    <Paper className={classes.root} variant="outlined">
      <div className={classes.stamp} />
      <div className={classes.lines} />
      <Box className={classes.addressBlock} aria-hidden="true">
        <Typography variant="body2" className={classes.addressLine}>TO: Santa Claus</Typography>
        <Typography variant="body2" className={classes.addressLine}>North Pole</Typography>
        <Typography variant="body2" className={classes.addressLine}>Arctic Circle</Typography>
        <Typography variant="body2" className={classes.addressLine}>Earth</Typography>
      </Box>
      <Box className={classes.content}>
        <Typography variant="subtitle1" className={classes.header}>
          Letter to Santa{from?.name ? ` from ${from?.name}` : ''}
        </Typography>
        {editable ? (
          <textarea
            aria-label="Write your wishes to Santa"
            placeholder={wishesPlaceholder}
            disabled={disabled}
            value={value ?? ''}
            onChange={onChange}
            className={classes.textStyle}
            rows={8}
          />
        ) : (
          <Typography className={classes.textStyle}>{wishes || '— No wishes provided —'}</Typography>
        )}
        <Typography variant="caption" className={classes.footer}>
          Ho-ho-ho! Make it joyful and kind.
        </Typography>
      </Box>
    </Paper>
  );
}
